import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Award } from 'lucide-react';
import { api } from '../../lib/api';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { CoachAnalyzeTab } from './CoachAnalyzeTab';
import { CoachCallsList } from './CoachCallsList';
import { CoachChat } from './CoachChat';
import { CoachReviewDialog } from './CoachReviewDialog';
import { useCoachT } from '../../lib/coachI18n';

export const STEPS_EVENT = 'coach:steps-updated';

/* Coach AI nella scheda cliente: analisi call, storico valutazioni, scrittura testi */
export const CoachPanel = ({ lead, onUpdated }) => {
  const { t } = useCoachT();
  const [tab, setTab] = useState('analyze');
  const [calls, setCalls] = useState([]);
  const [openCall, setOpenCall] = useState(null);
  const [denied, setDenied] = useState(false);
  const watching = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/coach/leads/${lead.id}/calls`);
      setCalls(r.data);
      return r.data;
    } catch (e) {
      if (e?.response?.status === 403) setDenied(true);
      return [];
    }
  }, [lead.id]);

  useEffect(() => { load(); }, [load]);

  const busyId = calls.find((c) => ['transcribing', 'analyzing'].includes(c.status))?.id;
  useEffect(() => {
    if (!busyId) return undefined;
    const iv = setInterval(async () => {
      const list = await load();
      const c = list.find((x) => x.id === watching.current);
      if (c && c.status === 'ready') {
        watching.current = null;
        setOpenCall(c);
      }
    }, 4000);
    return () => clearInterval(iv);
  }, [busyId, load]);

  const started = (call) => {
    watching.current = call.id;
    setCalls((prev) => [call, ...prev.filter((c) => c.id !== call.id)]);
  };

  const applied = () => {
    load();
    onUpdated?.();
    window.dispatchEvent(new CustomEvent(STEPS_EVENT, { detail: { leadId: lead.id } }));
  };

  if (denied) return null;

  return (
    <section className="rounded-xl border border-[#0F2A22]/15 overflow-hidden bg-[var(--surface-1)] shadow-sm" data-testid="coach-panel">
      <header className="px-4 py-3 bg-[#0F2A22] text-[#F4EBD0] flex items-center gap-3">
        <div className="w-9 h-9 rounded-full bg-[#C9A227] text-[#0F2A22] flex items-center justify-center"><Award className="w-5 h-5" /></div>
        <div>
          <div className="font-serif text-lg leading-none" data-testid="coach-panel-title">{t('title')}</div>
          <div className="text-[11px] text-[#F4EBD0]/70 mt-0.5">{t('subtitle')}</div>
        </div>
      </header>
      <Tabs value={tab} onValueChange={setTab} className="p-4">
        <TabsList className="mb-3">
          <TabsTrigger value="analyze" data-testid="coach-tab-analyze">{t('tabAnalyze')}</TabsTrigger>
          <TabsTrigger value="history" data-testid="coach-tab-history">{t('tabHistory')} {calls.length > 0 && `(${calls.length})`}</TabsTrigger>
          <TabsTrigger value="write" data-testid="coach-tab-write">{t('tabWrite')}</TabsTrigger>
        </TabsList>
        <TabsContent value="analyze"><CoachAnalyzeTab lead={lead} latest={calls[0]} onStarted={started} /></TabsContent>
        <TabsContent value="history"><CoachCallsList calls={calls} onOpen={setOpenCall} onChanged={load} /></TabsContent>
        <TabsContent value="write"><CoachChat leadId={lead.id} /></TabsContent>
      </Tabs>
      <CoachReviewDialog call={openCall} onClose={() => setOpenCall(null)} onApplied={applied} />
    </section>
  );
};
